import {
    AFORO_AREAS,
    AforoAreaId,
    AforoZone,
    AFORO_HORARIO,
    AFORO_ZONES,
} from "../Aforo/aforoConfig";
import { AforoSimulator, formatDateKeyEs } from "../Aforo/AforoSimulator";
import { aforoReservations } from "../Aforo/aforoReservations";
import {
    ENERGY_AREA_POR_DEFECTO_M2,
    ENERGY_CARGADOR_USO,
    ENERGY_CLIMA_CARGA,
    ENERGY_CO2,
    ENERGY_CONSIGNA,
    ENERGY_ELEMENTOS,
    ENERGY_ESTADO_COLORES,
    ENERGY_EXTERIOR,
    ENERGY_FV,
    ENERGY_HORARIOS,
    ENERGY_MANTENIMIENTO,
    ENERGY_SMOOTHING_SECONDS,
    ENERGY_TARIFA,
    ENERGY_UMBRALES,
    ENERGY_VENTILACION,
    ENERGY_VIEW_INFO,
    ENERGY_ZONE_OVERRIDES,
    ENERGY_ZONE_PARAMS,
    EnergyView,
    EnergyZoneParams,
    co2ToColor,
    consumoToColor,
    desperdicioToColor,
    fmtGrados,
    fmtKw,
    fmtPpm,
    fmtWm2,
    temperaturaToColor,
} from "./energyConfig";

// Simulador de energía: CO₂, temperatura y consumo de cada zona DERIVADOS de la
// ocupación del simulador de aforo. No tiene reloj propio ni datos propios:
// para cada fecha y hora pregunta al aforo cuánta gente hay (previsión más
// reservas) y calcula a partir de ahí. La misma fecha produce siempre los
// mismos datos, igual que el aforo.
//
// Modelo (por zona y media hora):
//  - CO₂: el aire exterior más lo que genera la gente, diluido por la
//    ventilación (infiltración sin clima; caudal normal con clima; caudal
//    reforzado si el CO₂ se pasa de "cargado"), con inercia.
//  - Temperatura: consigna del clima más el calor de la gente; sin clima
//    deriva hacia la exterior.
//  - Consumo: clima (según exterior y ocupación), luz (programada por
//    horario, sin detectores de presencia), fuerza (por persona y en espera)
//    y cargadores (por plaza eléctrica ocupada).
//  - Desperdicio: clima y luz encendidos en una zona SIN NADIE.

export interface EnergyZonePoint {
    hour: number;
    personas: number;
    co2: number; // ppm
    temperatura: number; // °C
    exterior: number; // °C
    kwClima: number;
    kwLuz: number;
    kwFuerza: number;
    kwCargadores: number;
    kw: number;
    kwDesperdicio: number;
    // Lo que consumiría sin gestión por ocupación (clima a plena carga en
    // todo su horario): la diferencia con `kw` es el ahorro conseguido.
    kwBase: number;
    climaOn: boolean;
    luzOn: boolean;
    // Ventilación reforzada por CO₂ alto.
    ventilacionReforzada: boolean;
}

export interface EnergyAreaPoint {
    hour: number;
    personas: number;
    kw: number;
    kwClima: number;
    kwLuz: number;
    kwFuerza: number;
    kwCargadores: number;
    kwDesperdicio: number;
    // CO₂: el peor de la planta (una sala cargada no se diluye en la media)
    // y la media ponderada por superficie.
    co2Max: number;
    co2Media: number;
    // Temperatura media ponderada por superficie.
    temperatura: number;
    exterior: number;
}

export interface EnergyDayTotals {
    kwh: number;
    kwhClima: number;
    kwhLuz: number;
    kwhFuerza: number;
    kwhCargadores: number;
    kwhDesperdicio: number;
    // Consumo sin gestión por ocupación (ver EnergyZonePoint.kwBase).
    kwhBase: number;
    coste: number;
    costeDesperdicio: number;
    co2Kg: number;
    // Pico de CO₂ del día: en qué zona y a qué hora.
    co2Max: { ppm: number; zoneId: string; zoneNombre: string; hour: number } | null;
    // Horas·zona con el aire cargado (por encima del umbral).
    horasCargado: number;
    tempMedia: number;
    // Zonas que más desperdician, de mayor a menor (kWh del día).
    desperdicioPorZona: Array<{ zoneId: string; zoneNombre: string; kwh: number }>;
}

const STEP_HOURS = 0.5;

/* eslint-disable no-bitwise */
const hashSeed = (text: string): number => {
    let h = 1779033703 ^ text.length;
    for (let i = 0; i < text.length; i += 1) {
        h = Math.imul(h ^ text.charCodeAt(i), 3432918353);
        h = (h << 13) | (h >>> 19);
    }
    return h >>> 0;
};
/* eslint-enable no-bitwise */

const clamp = (value: number, min: number, max: number): number =>
    Math.max(min, Math.min(max, value));

const isWeekend = (dateKey: string): boolean => {
    const day = new Date(`${dateKey}T12:00:00`).getDay();
    return day === 0 || day === 6;
};

const monthOf = (dateKey: string): number => Number(dateKey.split("-")[1]) - 1;

const inHorario = (hour: number, horario: { inicio: number; fin: number }): boolean =>
    hour >= horario.inicio && hour < horario.fin;

const formatHour = (hour: number): string => {
    const hh = Math.floor(hour) % 24;
    const mm = Math.floor((hour % 1) * 60);
    return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

export class EnergySimulator {
    private aforo: AforoSimulator;

    private profileCache: { [key: string]: EnergyZonePoint[] } = {};

    // Valores en vivo suavizados por zona (como el runtime del aforo).
    private runtime: { [zoneId: string]: { values: EnergyZonePoint; lastMs: number } } = {};

    constructor(aforo: AforoSimulator) {
        this.aforo = aforo;
        // La ocupación incluye las reservas: al cambiar una, los perfiles del
        // día se recalculan.
        aforoReservations.onChange(() => {
            this.profileCache = {};
        });
    }

    // ---- Datos de la zona ----

    private static zoneById = (zoneId: string): AforoZone | undefined =>
        AFORO_ZONES.find((z) => z.id === zoneId);

    static paramsOf = (zone: AforoZone): EnergyZoneParams => ({
        ...ENERGY_ZONE_PARAMS[zone.tipo],
        ...(ENERGY_ZONE_OVERRIDES[zone.id] ?? {}),
    });

    /**
     * Superficie de la zona (m²) a partir de sus rectángulos o plazas; por
     * defecto si no hay. Las medidas de aforoConfig van en metros (GLB).
     */
    static areaM2 = (zone: AforoZone): number => {
        if (zone.plazas) {
            const n = Array.isArray(zone.plazas) ? zone.plazas.length : zone.plazas.n;
            const plaza = (zone.plazaAncho ?? 2.5) * (zone.plazaFondo ?? 5);
            // Cada plaza más su parte del carril.
            return n * plaza * 1.6;
        }
        if (zone.rects && zone.rects.length > 0) {
            return zone.rects.reduce((acc, r) => acc + r.w * r.d, 0);
        }
        return ENERGY_AREA_POR_DEFECTO_M2;
    };

    // ---- Exterior y consigna ----

    static consigna = (dateKey: string): number =>
        ENERGY_CONSIGNA.mesesVerano.includes(monthOf(dateKey))
            ? ENERGY_CONSIGNA.verano
            : ENERGY_CONSIGNA.invierno;

    /** Temperatura exterior (°C) en una fecha y hora: media del mes, ciclo diario y variación del día. */
    static exteriorTemp = (dateKey: string, hour: number): number => {
        const media = ENERGY_EXTERIOR.mediaMensual[monthOf(dateKey)] ?? 15;
        // Determinista por fecha: un día más frío o más cálido que la media.
        const dia = ((hashSeed(`ext|${dateKey}`) % 1000) / 1000 - 0.5) * 2;
        const ciclo = Math.sin(((hour - 9) / 24) * Math.PI * 2);
        return (
            media + dia * ENERGY_EXTERIOR.variacionDiaria + ciclo * ENERGY_EXTERIOR.amplitudDiaria
        );
    };

    /**
     * Potencia fotovoltaica (kW) de la cubierta en una fecha y hora: campana
     * entre la salida y la puesta del sol del mes, con la irradiación del mes
     * y una nubosidad determinista por fecha (0,55..1).
     */
    static fvKw = (dateKey: string, hour: number): number => {
        const mes = monthOf(dateKey);
        const salida = ENERGY_FV.salida[mes] ?? 7.5;
        const puesta = ENERGY_FV.puesta[mes] ?? 19.5;
        if (hour <= salida || hour >= puesta) return 0;
        const campana = Math.sin(((hour - salida) / (puesta - salida)) * Math.PI);
        const nubes = 0.55 + ((hashSeed(`fv|${dateKey}`) % 1000) / 1000) * 0.45;
        const factor = ENERGY_FV.factorMensual[mes] ?? 0.8;
        return ENERGY_FV.kwp * ENERGY_FV.rendimiento * factor * nubes * campana;
    };

    /** Producción fotovoltaica del día por medias horas (mismo eje que los perfiles). */
    getFvDayProfile = (dateKey: string): Array<{ hour: number; kw: number }> => {
        const puntos: Array<{ hour: number; kw: number }> = [];
        for (let hour = AFORO_HORARIO.inicio; hour <= AFORO_HORARIO.fin; hour += STEP_HOURS) {
            puntos.push({ hour, kw: EnergySimulator.fvKw(dateKey, hour) });
        }
        return puntos;
    };

    // ---- Perfil del día de una zona ----

    getZoneDayProfile = (dateKey: string, zoneId: string): EnergyZonePoint[] => {
        const cacheKey = `${dateKey}|${zoneId}`;
        const cached = this.profileCache[cacheKey];
        if (cached) return cached;

        const zone = EnergySimulator.zoneById(zoneId);
        if (!zone) return [];
        const params = EnergySimulator.paramsOf(zone);
        const area = EnergySimulator.areaM2(zone);
        const volumen = area * params.altura;
        const laborable = !isWeekend(dateKey);
        const consigna = EnergySimulator.consigna(dateKey);
        const horarioLuz =
            zone.tipo === "parking" ? ENERGY_HORARIOS.parkingLuz : ENERGY_HORARIOS.luz;
        // Puestos en espera solo donde hay puestos (no en pasillos ni parking).
        const puestos =
            params.fuerzaStandbyPorPuesto > 0 && !zone.soloEtiqueta ? zone.aforoMaximo : 0;

        const ocupacion = this.aforo.getZoneDayProfile(dateKey, zoneId);
        const profile: EnergyZonePoint[] = [];
        let co2 = ENERGY_CO2.exterior + 20;
        let temperatura =
            EnergySimulator.exteriorTemp(dateKey, AFORO_HORARIO.inicio) * 0.45 + 20 * 0.55;

        ocupacion.forEach(({ hour, personas }) => {
            const exterior = EnergySimulator.exteriorTemp(dateKey, hour);
            const climaOn =
                params.clima > 0 &&
                params.climaProgramado &&
                laborable &&
                inHorario(hour, ENERGY_HORARIOS.clima);
            const luzOn = params.luz > 0 && laborable && inHorario(hour, horarioLuz);

            // CO₂ con inercia: renovaciones según la ventilación en marcha.
            const reforzada = climaOn && co2 > ENERGY_CO2.cargado;
            let ach = ENERGY_VENTILACION.infiltracion;
            if (climaOn) {
                ach = reforzada ? ENERGY_VENTILACION.reforzada : ENERGY_VENTILACION.normal;
            }
            const co2PorOcupante = params.co2PorOcupante ?? ENERGY_CO2.porPersona;
            const estable =
                ENERGY_CO2.exterior + ((personas * co2PorOcupante) / (volumen * ach)) * 1_000_000;
            co2 += (estable - co2) * (1 - Math.exp(-ach * STEP_HOURS));

            // Temperatura: la gente calienta; el clima lo compensa en parte.
            const ganancia = Math.min(3, (personas * 7.5) / area);
            const objetivo = climaOn
                ? consigna + ganancia * 0.35
                : exterior * 0.45 + 20 * 0.55 + ganancia * 0.6;
            const k = climaOn ? 2.5 : 0.35;
            temperatura += (objetivo - temperatura) * (1 - Math.exp(-k * STEP_HOURS));

            // Consumo.
            const ratio = zone.aforoMaximo > 0 ? Math.min(1, personas / zone.aforoMaximo) : 0;
            const factorExterior = clamp(Math.abs(exterior - consigna) / 12, 0.3, 1.3);
            const kwClima = climaOn
                ? ((area * params.clima * factorExterior) / 1000) *
                  (ENERGY_CLIMA_CARGA.minima + ENERGY_CLIMA_CARGA.porOcupacion * ratio) *
                  (reforzada ? 1.25 : 1)
                : 0;
            const kwLuz = luzOn ? (area * params.luz) / 1000 : 0;
            const kwFuerza =
                (personas * params.fuerzaPorPersona +
                    puestos * params.fuerzaStandbyPorPuesto +
                    area * params.fuerzaBase) /
                1000;
            const kwCargadores = params.cargadorKw
                ? personas * params.cargadorKw * ENERGY_CARGADOR_USO
                : 0;
            const kwDesperdicio = personas <= 0 ? kwClima + kwLuz : 0;
            const kwClimaBase = climaOn ? (area * params.clima * factorExterior) / 1000 : 0;

            profile.push({
                hour,
                personas,
                co2,
                temperatura,
                exterior,
                kwClima,
                kwLuz,
                kwFuerza,
                kwCargadores,
                kw: kwClima + kwLuz + kwFuerza + kwCargadores,
                kwDesperdicio,
                kwBase: kwClimaBase + kwLuz + kwFuerza + kwCargadores,
                climaOn,
                luzOn,
                ventilacionReforzada: reforzada,
            });
        });

        if (Object.keys(this.profileCache).length > 600) this.profileCache = {};
        this.profileCache[cacheKey] = profile;
        return profile;
    };

    /** Valores de una zona a una hora cualquiera del día (interpolados entre medias horas). */
    getZonePointAt = (dateKey: string, zoneId: string, hour: number): EnergyZonePoint | null => {
        const profile = this.getZoneDayProfile(dateKey, zoneId);
        if (profile.length === 0) return null;
        if (hour <= profile[0].hour) return profile[0];
        const last = profile[profile.length - 1];
        if (hour >= last.hour) return last;
        const index = Math.floor((hour - profile[0].hour) / STEP_HOURS);
        const a = profile[index];
        const b = profile[Math.min(index + 1, profile.length - 1)];
        const t = (hour - a.hour) / STEP_HOURS;
        const mix = (x: number, y: number) => x + (y - x) * t;
        const near = t < 0.5 ? a : b;
        return {
            hour,
            personas: mix(a.personas, b.personas),
            co2: mix(a.co2, b.co2),
            temperatura: mix(a.temperatura, b.temperatura),
            exterior: mix(a.exterior, b.exterior),
            kwClima: mix(a.kwClima, b.kwClima),
            kwLuz: mix(a.kwLuz, b.kwLuz),
            kwFuerza: mix(a.kwFuerza, b.kwFuerza),
            kwCargadores: mix(a.kwCargadores, b.kwCargadores),
            kw: mix(a.kw, b.kw),
            kwDesperdicio: mix(a.kwDesperdicio, b.kwDesperdicio),
            kwBase: mix(a.kwBase, b.kwBase),
            climaOn: near.climaOn,
            luzOn: near.luzOn,
            ventilacionReforzada: near.ventilacionReforzada,
        };
    };

    // ---- Planta entera ----

    /** Zonas que cuentan en la energía de una planta (las mismas que en el aforo). */
    getZones = (areaId: AforoAreaId): AforoZone[] => this.aforo.getZones(areaId);

    getAreaDayProfile = (dateKey: string, areaId: AforoAreaId): EnergyAreaPoint[] => {
        const zones = this.getZones(areaId);
        const profiles = zones.map((z) => ({
            area: EnergySimulator.areaM2(z),
            points: this.getZoneDayProfile(dateKey, z.id),
        }));
        if (profiles.length === 0 || profiles[0].points.length === 0) return [];
        const areaTotal = profiles.reduce((acc, p) => acc + p.area, 0) || 1;
        return profiles[0].points.map((first, i) => {
            const point: EnergyAreaPoint = {
                hour: first.hour,
                personas: 0,
                kw: 0,
                kwClima: 0,
                kwLuz: 0,
                kwFuerza: 0,
                kwCargadores: 0,
                kwDesperdicio: 0,
                co2Max: 0,
                co2Media: 0,
                temperatura: 0,
                exterior: first.exterior,
            };
            profiles.forEach(({ area, points }) => {
                const p = points[i];
                if (!p) return;
                point.personas += p.personas;
                point.kw += p.kw;
                point.kwClima += p.kwClima;
                point.kwLuz += p.kwLuz;
                point.kwFuerza += p.kwFuerza;
                point.kwCargadores += p.kwCargadores;
                point.kwDesperdicio += p.kwDesperdicio;
                point.co2Max = Math.max(point.co2Max, p.co2);
                point.co2Media += (p.co2 * area) / areaTotal;
                point.temperatura += (p.temperatura * area) / areaTotal;
            });
            return point;
        });
    };

    getDayTotals = (dateKey: string, areaId: AforoAreaId): EnergyDayTotals => {
        const zones = this.getZones(areaId);
        const totals: EnergyDayTotals = {
            kwh: 0,
            kwhClima: 0,
            kwhLuz: 0,
            kwhFuerza: 0,
            kwhCargadores: 0,
            kwhDesperdicio: 0,
            kwhBase: 0,
            coste: 0,
            costeDesperdicio: 0,
            co2Kg: 0,
            co2Max: null,
            horasCargado: 0,
            tempMedia: 0,
            desperdicioPorZona: [],
        };
        let tempSum = 0;
        let tempCount = 0;
        zones.forEach((zone) => {
            const profile = this.getZoneDayProfile(dateKey, zone.id);
            let desperdicioZona = 0;
            profile.forEach((p) => {
                totals.kwh += p.kw * STEP_HOURS;
                totals.kwhClima += p.kwClima * STEP_HOURS;
                totals.kwhLuz += p.kwLuz * STEP_HOURS;
                totals.kwhFuerza += p.kwFuerza * STEP_HOURS;
                totals.kwhCargadores += p.kwCargadores * STEP_HOURS;
                totals.kwhDesperdicio += p.kwDesperdicio * STEP_HOURS;
                totals.kwhBase += p.kwBase * STEP_HOURS;
                desperdicioZona += p.kwDesperdicio * STEP_HOURS;
                if (p.co2 > ENERGY_CO2.cargado) totals.horasCargado += STEP_HOURS;
                if (!totals.co2Max || p.co2 > totals.co2Max.ppm) {
                    totals.co2Max = {
                        ppm: p.co2,
                        zoneId: zone.id,
                        zoneNombre: zone.nombre,
                        hour: p.hour,
                    };
                }
                tempSum += p.temperatura;
                tempCount += 1;
            });
            if (desperdicioZona > 0.05) {
                totals.desperdicioPorZona.push({
                    zoneId: zone.id,
                    zoneNombre: zone.nombre,
                    kwh: desperdicioZona,
                });
            }
        });
        totals.desperdicioPorZona.sort((a, b) => b.kwh - a.kwh);
        totals.coste = totals.kwh * ENERGY_TARIFA.precioKwh;
        totals.costeDesperdicio = totals.kwhDesperdicio * ENERGY_TARIFA.precioKwh;
        totals.co2Kg = totals.kwh * ENERGY_TARIFA.factorEmision;
        totals.tempMedia = tempCount > 0 ? tempSum / tempCount : 0;
        return totals;
    };

    // ---- En vivo (lo que se pinta sobre el plano) ----

    /** Olvida el suavizado: el siguiente fotograma pinta los valores reales. */
    resetSmoothing = (): void => {
        this.runtime = {};
    };

    /** Valores actuales de una zona según la foto (o el replay) del aforo, suavizados. */
    getZoneLive = (zoneId: string, nowMs: number = performance.now()): EnergyZonePoint | null => {
        const { dateKey } = this.aforo.getSnapshot();
        const hour = this.aforo.getSimHour(nowMs);
        const target = this.getZonePointAt(dateKey, zoneId, hour);
        if (!target) return null;
        let state = this.runtime[zoneId];
        if (!state) {
            state = { values: { ...target }, lastMs: nowMs };
            this.runtime[zoneId] = state;
            return state.values;
        }
        const dt = Math.max(0, (nowMs - state.lastMs) / 1000);
        const alpha = 1 - Math.exp(-dt / ENERGY_SMOOTHING_SECONDS);
        const v = state.values;
        const ease = (key: keyof EnergyZonePoint) => {
            const current = v[key] as number;
            const wanted = target[key] as number;
            (v[key] as number) = current + (wanted - current) * alpha;
        };
        (
            [
                "personas",
                "co2",
                "temperatura",
                "exterior",
                "kwClima",
                "kwLuz",
                "kwFuerza",
                "kwCargadores",
                "kw",
                "kwDesperdicio",
            ] as Array<keyof EnergyZonePoint>
        ).forEach(ease);
        v.hour = target.hour;
        v.climaOn = target.climaOn;
        v.luzOn = target.luzOn;
        v.ventilacionReforzada = target.ventilacionReforzada;
        state.lastMs = nowMs;
        return v;
    };

    /** Magnitud de la vista en una zona (ppm, °C o W/m²), a partir de sus valores en vivo. */
    static viewValue = (zone: AforoZone, live: EnergyZonePoint, view: EnergyView): number => {
        const area = EnergySimulator.areaM2(zone);
        switch (view) {
            case "co2":
                return live.co2;
            case "temperatura":
                return live.temperatura;
            case "consumo":
                return (live.kw * 1000) / area;
            default:
                return (live.kwDesperdicio * 1000) / area;
        }
    };

    /**
     * Valor 0..1 para el sombreador de un punto de aforo en la vista dada. Las
     * plazas de parking con cargador se pintan una a una (el cargador consume
     * solo con coche).
     */
    getHeatValue = (zoneId: string, view: EnergyView, plaza?: number): number => {
        const zone = EnergySimulator.zoneById(zoneId);
        const live = this.getZoneLive(zoneId);
        if (!zone || !live) return 0;
        const info = ENERGY_VIEW_INFO[view];
        let value = EnergySimulator.viewValue(zone, live, view);
        if (plaza !== undefined && view === "consumo" && zone.plazas) {
            const params = EnergySimulator.paramsOf(zone);
            if (params.cargadorKw) {
                // Plaza con cargador: a tope con coche, solo la luz sin él.
                const ocupada = this.aforo.getPlazaValue(zoneId, plaza) > 0.5;
                const areaPlaza = EnergySimulator.areaM2(zone) / Math.max(1, zone.aforoMaximo);
                value = ocupada
                    ? (params.cargadorKw * 1000) / areaPlaza
                    : (live.kwLuz * 1000) / EnergySimulator.areaM2(zone);
            }
        }
        return clamp((value - info.min) / (info.max - info.min), 0, 1);
    };

    /** Texto y color de la chip de una zona en la vista dada. */
    getZoneChip = (zoneId: string, view: EnergyView): { detalle: string; color: string } | null => {
        const zone = EnergySimulator.zoneById(zoneId);
        const live = this.getZoneLive(zoneId);
        if (!zone || !live) return null;
        switch (view) {
            case "co2":
                return {
                    detalle: live.ventilacionReforzada
                        ? `${fmtPpm(live.co2)} · ventilando`
                        : fmtPpm(live.co2),
                    color: co2ToColor(live.co2),
                };
            case "temperatura":
                return {
                    detalle: live.climaOn
                        ? fmtGrados(live.temperatura)
                        : `${fmtGrados(live.temperatura)} · sin clima`,
                    color: temperaturaToColor(live.temperatura),
                };
            case "consumo": {
                const wm2 = EnergySimulator.viewValue(zone, live, view);
                return {
                    detalle: `${fmtKw(live.kw)} · ${fmtWm2(wm2)}`,
                    color: consumoToColor(wm2),
                };
            }
            case "luminarias": {
                if (!live.luzOn || live.kwLuz <= 0) {
                    return { detalle: "apagadas", color: ENERGY_ESTADO_COLORES.luzApagada };
                }
                return live.personas > 0.5
                    ? {
                          detalle: `${fmtKw(live.kwLuz)} · con gente`,
                          color: ENERGY_ESTADO_COLORES.luzGente,
                      }
                    : {
                          detalle: `${fmtKw(live.kwLuz)} · sin nadie`,
                          color: ENERGY_ESTADO_COLORES.luzSinNadie,
                      };
            }
            case "fuerza": {
                const ocupados = Math.round(live.personas);
                return {
                    detalle: `${fmtKw(live.kwFuerza)} · ${ocupados} ${
                        ocupados === 1 ? "puesto ocupado" : "puestos ocupados"
                    }`,
                    color:
                        ocupados > 0
                            ? ENERGY_ESTADO_COLORES.puestoOcupado
                            : ENERGY_ESTADO_COLORES.puestoEspera,
                };
            }
            case "cargadores": {
                if (zoneId !== ENERGY_ELEMENTOS.zonaCargadores)
                    return { detalle: "", color: "#0B2739" };
                const cargando = Math.round(live.personas);
                return {
                    detalle:
                        cargando > 0
                            ? `${fmtKw(live.kwCargadores)} · ${cargando} cargando`
                            : "libres",
                    color:
                        cargando > 0
                            ? ENERGY_ESTADO_COLORES.cargando
                            : ENERGY_ESTADO_COLORES.cargadorLibre,
                };
            }
            case "caldera":
            case "agua":
            case "placas":
            case "incidencia":
            case "mantenimiento":
                return { detalle: "", color: "#0B2739" };
            default: {
                if (live.kwDesperdicio <= 0.01) {
                    return { detalle: "Sin desperdicio", color: desperdicioToColor(0) };
                }
                const que: string[] = [];
                if (live.kwLuz > 0) que.push("luz");
                if (live.kwClima > 0) que.push("clima");
                return {
                    detalle: `${fmtKw(live.kwDesperdicio)} · ${que.join(" y ")} sin nadie`,
                    color: desperdicioToColor(live.kwDesperdicio),
                };
            }
        }
    };

    /** Texto del chip superior del visor. */
    getStatusLabel = (view: EnergyView, nowMs: number = performance.now()): string => {
        const { dateKey } = this.aforo.getSnapshot();
        const hour = this.aforo.getSimHour(nowMs);
        const exterior = EnergySimulator.exteriorTemp(dateKey, hour);
        return `${ENERGY_VIEW_INFO[view].nombre.toUpperCase()} · ${this.aforo.getStatusLabel(
            nowMs
        )} · exterior ${fmtGrados(exterior)}`;
    };

    // ---- Instalaciones: elementos del modelo (luminarias, puestos, cargadores, caldera, placas) ----

    /** Estado de una luminaria por su zona o, si no cae en ninguna, por el horario de la planta. */
    getLuminariaEstado = (
        areaId: AforoAreaId,
        zoneId: string | null,
        nowMs: number = performance.now()
    ): "gente" | "sinNadie" | "apagada" => {
        if (zoneId) {
            const live = this.getZoneLive(zoneId, nowMs);
            if (live) {
                if (!live.luzOn) return "apagada";
                return live.personas > 0.5 ? "gente" : "sinNadie";
            }
        }
        const { dateKey } = this.aforo.getSnapshot();
        const hour = this.aforo.getSimHour(nowMs);
        const horario = areaId === "parking" ? ENERGY_HORARIOS.parkingLuz : ENERGY_HORARIOS.luz;
        if (isWeekend(dateKey) || !inHorario(hour, horario)) return "apagada";
        // Pasillos y zonas comunes sin zona: con gente en la planta no es desperdicio.
        const alguien = this.getZones(areaId).some(
            (z) => (this.getZoneLive(z.id, nowMs)?.personas ?? 0) > 0.5
        );
        return alguien ? "gente" : "sinNadie";
    };

    /** Un puesto (el i-ésimo de su zona) está ocupado si la zona tiene al menos i+1 personas. */
    getPuestoOcupado = (
        zoneId: string,
        index: number,
        nowMs: number = performance.now()
    ): boolean => index < Math.round(this.getZoneLive(zoneId, nowMs)?.personas ?? 0);

    /** Cargador de una plaza eléctrica: si carga ahora, a qué potencia y lo cargado en el día. */
    getCargadorLive = (
        plaza: number,
        nowMs: number = performance.now()
    ): { ocupada: boolean; kw: number; kwhHoy: number } => {
        const zoneId = ENERGY_ELEMENTOS.zonaCargadores;
        const zone = EnergySimulator.zoneById(zoneId);
        const kwPlaza =
            (zone ? EnergySimulator.paramsOf(zone).cargadorKw ?? 7.4 : 7.4) * ENERGY_CARGADOR_USO;
        const ocupada = this.aforo.getPlazaValue(zoneId, plaza, nowMs) > 0.5;
        const { dateKey } = this.aforo.getSnapshot();
        const hour = this.aforo.getSimHour(nowMs);
        // La plaza k está ocupada en los tramos del día con más de k coches.
        const kwhHoy = this.aforo
            .getZoneDayProfile(dateKey, zoneId)
            .filter((p) => p.hour < hour && p.personas >= plaza + 1)
            .reduce((acc) => acc + kwPlaza * STEP_HOURS, 0);
        return { ocupada, kw: ocupada ? kwPlaza : 0, kwhHoy };
    };

    /** Central de climatización: suma del clima de todas las zonas del edificio. */
    getCalderaLive = (
        nowMs: number = performance.now()
    ): { kw: number; kwhHoy: number; estado: string; color: string } => {
        const kw = AFORO_ZONES.filter((z) => !z.hidden && !z.soloEtiqueta).reduce(
            (acc, z) => acc + (this.getZoneLive(z.id, nowMs)?.kwClima ?? 0),
            0
        );
        const { dateKey } = this.aforo.getSnapshot();
        const kwhHoy = AFORO_AREAS.reduce(
            (acc, a) => acc + this.getDayTotals(dateKey, a.id).kwhClima,
            0
        );
        let estado = "Apagada";
        let color = ENERGY_ESTADO_COLORES.calderaApagada;
        if (kw > ENERGY_UMBRALES.calderaMedia) {
            estado = "Carga alta";
            color = ENERGY_ESTADO_COLORES.calderaAlta;
        } else if (kw > ENERGY_UMBRALES.calderaBaja) {
            estado = "Carga media";
            color = ENERGY_ESTADO_COLORES.calderaMedia;
        } else if (kw > 0.05) {
            estado = "Carga baja";
            color = ENERGY_ESTADO_COLORES.calderaBaja;
        }
        return { kw, kwhHoy, estado, color };
    };

    /** Potencia de una placa (W) a la hora de la foto, con el rendimiento propio de cada placa. */
    getPlacaLive = (
        index: number,
        nPlacas: number,
        nowMs: number = performance.now()
    ): { w: number; color: string } => {
        const { dateKey } = this.aforo.getSnapshot();
        const hour = this.aforo.getSimHour(nowMs);
        const rendimiento = 0.9 + (hashSeed(`placa|${index}`) % 21) / 100;
        const w = (EnergySimulator.fvKw(dateKey, hour) * 1000 * rendimiento) / Math.max(1, nPlacas);
        let color = ENERGY_ESTADO_COLORES.placaNoche;
        if (w > ENERGY_UMBRALES.placaMedia) color = ENERGY_ESTADO_COLORES.placaAlta;
        else if (w > ENERGY_UMBRALES.placaBaja) color = ENERGY_ESTADO_COLORES.placaMedia;
        else if (w > 1) color = ENERGY_ESTADO_COLORES.placaBaja;
        return { w, color };
    };

    /** Estado de mantenimiento de una placa: determinista por placa y mes, con fecha de detección. */
    static getMantenimiento = (
        index: number,
        dateKey: string
    ): { id: string; nombre: string; color: string; desde: string | null } => {
        const mes = dateKey.slice(0, 7);
        const r = (hashSeed(`mant|${index}|${mes}`) % 1000) / 1000;
        let acc = 0;
        const estado =
            ENERGY_MANTENIMIENTO.find((e) => {
                acc += e.peso;
                return r <= acc;
            }) ?? ENERGY_MANTENIMIENTO[0];
        if (estado.id === "bien") return { ...estado, desde: null };
        // Detectada entre 1 y 45 días antes del día elegido.
        const d = new Date(`${dateKey}T12:00:00`);
        d.setDate(d.getDate() - (1 + (hashSeed(`mantdia|${index}|${mes}`) % 45)));
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
            d.getDate()
        ).padStart(2, "0")}`;
        return { ...estado, desde: formatDateKeyEs(key) };
    };

    /** Perfil del edificio entero por medias horas (suma de las plantas). */
    getBuildingDayProfile = (dateKey: string): EnergyAreaPoint[] => {
        const perArea = AFORO_AREAS.map((a) => this.getAreaDayProfile(dateKey, a.id));
        const base = perArea.find((p) => p.length > 0) ?? [];
        return base.map((_, i) => {
            const puntos = perArea.map((p) => p[i]).filter(Boolean);
            const sum = (pick: (p: EnergyAreaPoint) => number) =>
                puntos.reduce((acc, p) => acc + pick(p), 0);
            const n = Math.max(1, puntos.length);
            return {
                hour: base[i].hour,
                personas: sum((p) => p.personas),
                kw: sum((p) => p.kw),
                kwClima: sum((p) => p.kwClima),
                kwLuz: sum((p) => p.kwLuz),
                kwFuerza: sum((p) => p.kwFuerza),
                kwCargadores: sum((p) => p.kwCargadores),
                kwDesperdicio: sum((p) => p.kwDesperdicio),
                co2Max: Math.max(...puntos.map((p) => p.co2Max)),
                co2Media: sum((p) => p.co2Media) / n,
                temperatura: sum((p) => p.temperatura) / n,
                exterior: base[i].exterior,
            };
        });
    };

    /** Balance fotovoltaico del día: generado, autoconsumido, vertido, ahorro y CO₂ evitado. */
    getFvDayTotals = (
        dateKey: string
    ): {
        kwh: number;
        auto: number;
        exc: number;
        ahorro: number;
        co2Kg: number;
        cobertura: number;
    } => {
        const carga = this.getBuildingDayProfile(dateKey);
        let kwh = 0;
        let auto = 0;
        let demanda = 0;
        carga.forEach((p) => {
            const gen = EnergySimulator.fvKw(dateKey, p.hour);
            kwh += gen * STEP_HOURS;
            auto += Math.min(gen, p.kw) * STEP_HOURS;
            demanda += p.kw * STEP_HOURS;
        });
        return {
            kwh,
            auto,
            exc: Math.max(0, kwh - auto),
            ahorro: auto * ENERGY_TARIFA.precioKwh,
            co2Kg: auto * ENERGY_TARIFA.factorEmision,
            cobertura: demanda > 0 ? auto / demanda : 0,
        };
    };

    static formatHour = formatHour;
}
